import { useState, useEffect, useCallback } from 'react';
import { Icon, Spinner } from '../ui/Icon.js';
import { Button } from '../ui/Button.js';
import { Dialog } from '../ui/Dialog.js';
import { apiGet } from '../../lib/api.js';
import { apiPost, apiPatch, apiDelete } from '../../lib/api-mutations.js';
import { useToast } from '../../hooks/useToast.js';
import { EmptyState } from '../EmptyState.js';
import type { Tag, TagListResponse } from '../../types/index.js';

const HEX_COLOR_RE = /^#[0-9A-Fa-f]{6}$/;

// These stay literal hex, not tokens: a tag's color is user-chosen content
// data round-tripped to the backend as a `#rrggbb` string (validated by
// HEX_COLOR_RE above) and bound to a native <input type="color">, which
// requires a resolvable color value — a `var(--hub-color-*)` reference would
// fail validation and can't be set as that input's value. This is a data
// palette, not a themed role, so it is exempt from the token sweep.
const COLOR_PRESETS = [
  '#EF4444',
  '#F97316',
  '#F59E0B',
  '#10B981',
  '#3B82F6',
  '#8B5CF6',
  '#EC4899',
  '#6B7280',
];

interface TagFormState {
  name: string;
  color: string;
}

export function TagManager({ tenantId }: { tenantId: string }) {
  const { addToast } = useToast();
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<TagFormState>({ name: '', color: COLOR_PRESETS[0] });
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const loadTags = useCallback(async () => {
    try {
      const res = await apiGet<TagListResponse>(`/api/v1/tenants/${tenantId}/tags`);
      setTags(res.data);
    } catch {
      addToast('Failed to load tags', 'error');
    } finally {
      setLoading(false);
    }
  }, [tenantId, addToast]);

  useEffect(() => {
    loadTags();
  }, [loadTags]);

  function validate(): boolean {
    const e: Record<string, string> = {};
    if (!form.name.trim()) e.name = 'Name is required';
    if (!HEX_COLOR_RE.test(form.color)) e.color = 'Invalid color format';
    setErrors(e);
    return Object.keys(e).length === 0;
  }

  async function handleCreate() {
    if (!validate()) return;
    setSaving(true);
    try {
      await apiPost(`/api/v1/tenants/${tenantId}/tags`, {
        name: form.name.trim(),
        color: form.color,
      });
      addToast('Tag created successfully', 'success');
      setShowCreate(false);
      setForm({ name: '', color: COLOR_PRESETS[0] });
      await loadTags();
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to create tag', 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handleUpdate(tagId: string) {
    if (!validate()) return;
    setSaving(true);
    try {
      await apiPatch(`/api/v1/tenants/${tenantId}/tags/${tagId}`, {
        name: form.name.trim(),
        color: form.color,
      });
      addToast('Tag updated successfully', 'success');
      setEditingId(null);
      // Optimistic update for color
      setTags((prev) =>
        prev.map((t) =>
          t.id === tagId ? { ...t, name: form.name.trim(), color: form.color } : t,
        ),
      );
      await loadTags();
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to update tag', 'error');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(tagId: string) {
    setDeleting(true);
    try {
      await apiDelete(`/api/v1/tenants/${tenantId}/tags/${tagId}`);
      addToast('Tag deleted successfully', 'success');
      setDeleteConfirm(null);
      setTags((prev) => prev.filter((t) => t.id !== tagId));
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Failed to delete tag', 'error');
    } finally {
      setDeleting(false);
    }
  }

  function startEdit(tag: Tag) {
    setEditingId(tag.id);
    setForm({ name: tag.name, color: tag.color });
    setShowCreate(false);
    setErrors({});
  }

  function cancelEdit() {
    setEditingId(null);
    setForm({ name: '', color: COLOR_PRESETS[0] });
    setErrors({});
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner className="text-gray-400" />
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-4 flex items-center justify-between">
        <p className="text-sm text-gray-500">{tags.length} tag{tags.length !== 1 ? 's' : ''}</p>
        <Button
          variant="primary"
          onClick={() => {
            setShowCreate(true);
            setEditingId(null);
            setForm({ name: '', color: COLOR_PRESETS[0] });
            setErrors({});
          }}
        >
          <Icon name="bx-plus" />
          Add tag
        </Button>
      </div>

      {/* Create form */}
      {showCreate && (
        <div className="mb-4 rounded-lg border border-violet-200 bg-violet-50 p-4">
          <TagFormInline
            form={form}
            setForm={setForm}
            errors={errors}
            saving={saving}
            onSave={handleCreate}
            onCancel={() => {
              setShowCreate(false);
              setErrors({});
            }}
            saveLabel="Create"
          />
        </div>
      )}

      {/* Tag list */}
      {tags.length === 0 && !showCreate ? (
        <EmptyState
          icon="bx-purchase-tag"
          variant="onboarding"
          title="No tags yet"
          description="Create your first tag to organize brands for filtering and grouping."
        />
      ) : (
        <div className="space-y-2">
          {tags.map((tag) => (
            <div key={tag.id} className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white p-3">
              {editingId === tag.id ? (
                <div className="flex-1">
                  <TagFormInline
                    form={form}
                    setForm={setForm}
                    errors={errors}
                    saving={saving}
                    onSave={() => handleUpdate(tag.id)}
                    onCancel={cancelEdit}
                    saveLabel="Save"
                  />
                </div>
              ) : (
                <>
                  <span
                    className="inline-flex items-center rounded-full px-3 py-1 text-xs font-medium text-white"
                    style={{ backgroundColor: tag.color }}
                  >
                    {tag.name}
                  </span>
                  <span className="text-xs text-gray-400">
                    {tag.brand_count} brand{tag.brand_count !== 1 ? 's' : ''}
                  </span>
                  <div className="ml-auto flex items-center gap-1">
                    <button
                      onClick={() => startEdit(tag)}
                      className="rounded-md p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                      title="Edit"
                    >
                      <Icon name="bx-pencil" />
                    </button>
                    <button
                      onClick={() => setDeleteConfirm(tag.id)}
                      className="rounded-md p-1.5 text-gray-400 hover:bg-red-50 hover:text-red-600"
                      title="Delete"
                    >
                      <Icon name="bx-trash" />
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Delete confirmation */}
      <Dialog
        open={deleteConfirm !== null}
        onClose={() => setDeleteConfirm(null)}
        title="Delete tag"
      >
        <p className="mt-2 text-sm text-[var(--hub-color-ink-muted)]">
          Are you sure? This tag will be removed from all brands.
        </p>
        <div className="mt-4 flex justify-end gap-3">
          <Button variant="ghost" onClick={() => setDeleteConfirm(null)}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={() => deleteConfirm && handleDelete(deleteConfirm)}
            disabled={deleting}
          >
            {deleting && <Spinner />}
            Delete
          </Button>
        </div>
      </Dialog>
    </div>
  );
}

function TagFormInline({
  form,
  setForm,
  errors,
  saving,
  onSave,
  onCancel,
  saveLabel,
}: {
  form: TagFormState;
  setForm: (f: TagFormState) => void;
  errors: Record<string, string>;
  saving: boolean;
  onSave: () => void;
  onCancel: () => void;
  saveLabel: string;
}) {
  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className="hub-field flex-1">
        <label className="hub-field__label">Name</label>
        <input
          type="text"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          className={`hub-input ${errors.name ? 'border-[var(--hub-color-danger)]' : ''}`}
          placeholder="Tag name"
        />
        {errors.name && (
          <p className="hub-field__help text-[var(--hub-color-danger)]">{errors.name}</p>
        )}
      </div>
      <div>
        <label className="block text-xs font-medium text-gray-600">Color</label>
        <div className="mt-1 flex items-center gap-1.5">
          {COLOR_PRESETS.map((c) => (
            <button
              key={c}
              type="button"
              onClick={() => setForm({ ...form, color: c })}
              className={`h-6 w-6 rounded-full border-2 transition-all ${
                form.color === c ? 'scale-110 border-gray-800' : 'border-transparent'
              }`}
              style={{ backgroundColor: c }}
            />
          ))}
          <input
            type="color"
            value={form.color}
            onChange={(e) => setForm({ ...form, color: e.target.value })}
            className="ml-1 h-6 w-6 cursor-pointer rounded-md border-0 p-0"
          />
        </div>
      </div>
      <div className="flex items-center gap-1">
        <Button variant="primary" onClick={onSave} disabled={saving}>
          {saving ? <Spinner /> : <Icon name="bx-check" />}
          {saveLabel}
        </Button>
        <Button round onClick={onCancel} aria-label="Cancel">
          <Icon name="bx-x" />
        </Button>
      </div>
    </div>
  );
}
